/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  IOSShelfAnalyzeRequest,
  AOSShelfAnalyzeRequest,
} from "@/app/api/shelf/shelf-analyze/analyze/_type/ShelfAnalyzeEntity"
declare global {
  interface Window {
    readlog?: {
      action: (message: string) => void
    }
    webkit?: {
      messageHandlers?: {
        readlog?: {
          postMessage?: (message: any) => void
        }
      }
    }
    nativeBr?: NativeBr
  }
}

interface NativeTestable {
  mocking(
    path: string,
    message: any,
    cb: (isFinished: boolean, ret: any) => void,
  ): void
}

interface NativeBrParamWv {
  url: string
}
type NativeBrParamDeeplink = NativeBrParamWv

class NativeBr {
  get isAOS(): boolean {
    if (typeof window === "undefined") return false
    return !!window.readlog
  }
  private sendMessageToAOS(message: any) {
    window.readlog!.action(JSON.stringify(message))
  }
  get isIOS(): boolean {
    if (typeof window === "undefined") return false
    return !!window.webkit?.messageHandlers?.readlog?.postMessage
  }
  private sendMessageToIOS(message: any) {
    window.webkit!.messageHandlers!.readlog!.postMessage!(message)
  }

  private _post: (message: any) => void
  private _index: number = 0
  private cb: any = {}

  private _isEnabled?: boolean
  get isEnabled(): boolean {
    if (this._isEnabled == undefined) {
      this._isEnabled = this.isAOS || this.isIOS
    }
    return this._isEnabled!
  }

  constructor(private proxy?: NativeTestable) {
    this._post = (message) => {
      if (this.isAOS) {
        this.sendMessageToAOS(message)
      }
      if (this.isIOS) {
        this.sendMessageToIOS(message)
      }
    }
  }

  private post<T>(message: any, ms: number = 10 * 1000): Promise<T> {
    this._index += 1
    const key = this._index
    const proxy = this.proxy
    const path = message["$path"] as string
    return new Promise((resolve, reject) => {
      this.cb[key] = {
        resolve,
        reject,
      }

      if (!this.isEnabled) {
        // 네이티브 환경이 아닐 때만 Mock 사용
        proxy?.mocking(path, message, (isFinished, ret) =>
          this._nativecb(key, isFinished, ret),
        )
      } else {
        // 네이티브 환경에서만 실제 호출
        setTimeout(
          () =>
            this._post({
              ...message,
              $cb: `nativeBr._nativecb(${key}, {0}, {1});`,
            }),
          15,
        )
      }

      setTimeout(
        () => this._nativecb(key, false, { path, message: "TIME_OUT" }),
        ms,
      )
    })
  }

  _nativecb(key: number, isFinished: boolean, ret: any) {
    const cb = this.cb[key]
    if (cb) {
      if (isFinished) {
        cb.resolve(ret)
      } else {
        cb.reject(ret)
      }
      delete this.cb[key]
    }
  }

  async wv(param: NativeBrParamWv): Promise<object> {
    return this.post({ $path: "common/wv", $param: param })
  }

  async deeplink(param: NativeBrParamDeeplink): Promise<object> {
    return this.post({ $path: "common/deeplink", $param: param }, 3 * 1000)
  }

  async dismiss(param: object = {}): Promise<object> {
    return this.post({ $path: "common/dismiss", $param: param }, 3 * 1000)
  }

  async OCRExtract(param: { from: "album" | "photo" }): Promise<{
    text: string
  }> {
    return this.post(
      { $path: `ocr/sentence/extract/from-${param.from}`, $param: param },
      60 * 1000, // 60초간 유지
    )
  }

  async barcodeScan(
    param: object = {},
  ): Promise<{ type: "ISBN"; scanned: string }> {
    return this.post(
      { $path: `barcode-scan`, $param: param },
      60 * 1000, // 60초간 유지
    )
  }

  async shelfPhoto(param: object = {}): Promise<{
    osType: "ios" | "android"
    text: IOSShelfAnalyzeRequest | AOSShelfAnalyzeRequest
  }> {
    return this.post(
      { $path: `shelf-photo`, $param: param },
      60 * 1000, // 60초간 유지
    )
  }

  async shelfPhotoAnalyzeId(param: object = {}): Promise<{ text: string }> {
    return this.post(
      { $path: `shelf-photo-analyze-id`, $param: param },
      60 * 1000, // 60초간 유지
    )
  }

  async shelfPhotoFinalizeId(param: object = {}): Promise<{ text: string }> {
    return this.post(
      { $path: `shelf-photo-finalize-id`, $param: param },
      60 * 1000, // 60초간 유지
    )
  }

  async openExternalUrl(param: { targetUrl: string }): Promise<void> {
    return this.post(
      { $path: "common/open-external-url", $param: param },
      3 * 1000,
    )
  }

  async openDGTUrl(param: { targetUrl: string }): Promise<void> {
    return this.post({ $path: "common/open-dgt-url", $param: param }, 3 * 1000)
  }

  async clipboardCopy(param: { text: string }): Promise<void> {
    return this.post(
      { $path: "common/clipboard/copy", $param: param },
      3 * 1000,
    )
  }

  async DGTsessionExpire(param: object = {}): Promise<object> {
    return this.post(
      { $path: "common/session/expire", $param: param },
      3 * 1000,
    )
  }

  async DGTsessionExtend(param: object = {}): Promise<object> {
    return this.post(
      { $path: "common/session/extend", $param: param },
      3 * 1000,
    )
  }

  async dfineryLogEvent(param: {
    eventName: string
    attrModel: Record<string, any>
  }): Promise<void> {
    return this.post(
      { $path: "common/log/dfinery", $param: param },
      3 * 1000,
    )
  }

  async appVersion(param: object = {}): Promise<{ version: string; osType: "android" | "ios" }> {
    return this.post(
      { $path: "common/app/version", $param: param },
      3 * 1000,
    )
  }
}

class NativeBrMocking implements NativeTestable {
  mocking(
    path: string,
    message: any,
    cb: (isResolve: boolean, ret: any) => void,
  ): void {
    console.log("message ", message)
    switch (path) {
      case "common/wv":
        cb(true, {})
        break
      case "common/deeplink":
        cb(true, {})
        break
      case "common/dismiss":
        cb(true, {})
        break
      case "ocr/sentence/extract/from-album":
        cb(false, {
          text: "(mocking)앨범의 사진으로 의해 문장을 추출해 가져온 문자열입니다.",
        })
        break
      case "ocr/sentence/extract/from-photo":
        cb(true, {
          text: "(mocking)카메라 사진 촬영으로 문장을 추출해 가져온 문자열입니다.",
        })
        break
      case "shelf-photo":
        cb(true, {
          text: {
            imageWidth: 5712,
            imageHeight: 4284,
            ocrTexts: [
              {
                bbox: {
                  y: 2029.3410333458835,
                  height: 1111.5215917968756,
                  x: 5498.111886831051,
                  width: 153.6765032958983,
                },
                text: "리더와 직원의 동상이몽",
              },
              {
                text: "하수미 지음",
                bbox: {
                  y: 1600.3448324277376,
                  x: 5527.3448179811585,
                  height: 258.5172381591799,
                  width: 73.86209083557146,
                },
              },
              {
                bbox: {
                  y: 2462.068971972616,
                  x: 5256.517225985154,
                  width: 61.55175338745114,
                  height: 344.68965087890587,
                },
                text: "상하좌오 리더십",
              },
              {
                bbox: {
                  width: 199.9850729370119,
                  y: 1829.6192003353694,
                  height: 1043.1275537109375,
                  x: 5142.077470125015,
                },
                text: "신화, 오을 활달했다. * 모두의 팀장",
              },
              {
                bbox: {
                  width: 61.551776733398796,
                  x: 5219.586179572955,
                  height: 664.758603515625,
                  y: 1157.1724334594282,
                },
                text: "* 김운경 김종원 노유진 서인수 오준엽•우시회",
              },
              {
                bbox: {
                  y: 2240.482772604815,
                  x: 4973.379273223428,
                  width: 98.48283966064463,
                  height: 861.7241271972656,
                },
                text: "리더는 목적을 먹고 산다",
              },
              {
                text: "은 배가 와 지금 | 김성님 옮김",
                bbox: {
                  height: 369.31031799316366,
                  x: 4997.999985469997,
                  width: 36.931068763732426,
                  y: 1292.5862295465208,
                },
              },
              {
                text: "세계 최고의 CEO는 어떻게 일하는가",
                bbox: {
                  y: 1735.7586325610314,
                  height: 1464.931047363281,
                  x: 4739.482697627723,
                  width: 123.10356903076163,
                },
              },
              {
                bbox: {
                  width: 61.551842880248984,
                  y: 1809.6207154766703,
                  height: 1378.7586035156244,
                  x: 4665.620629514617,
                },
                text: "캐롤린 듀어• 스콧 켈러 비크림 말호트라 지음 | 맥킨지 한국사무소, CEO 엑설런스 센터 감수 | 양진성 옮김",
              },
              {
                text: "CEO",
                bbox: {
                  y: 1267.9655174869677,
                  width: 86.17242828369177,
                  x: 4764.1034404150505,
                  height: 184.65517456054724,
                },
              },
              {
                text: "Bxcellence",
                bbox: {
                  height: 418.55171447753946,
                  width: 86.17244384765645,
                  y: 1144.8620772836437,
                  x: 4677.9310189425505,
                },
              },
              {
                text: "코더스",
                bbox: {
                  width: 164.70397705078167,
                  y: 2035.3669170913593,
                  x: 4335.533624966093,
                  height: 434.59766418456985,
                },
              },
              {
                bbox: {
                  height: 320.0689526367187,
                  y: 2745.2069069928475,
                  x: 4222.448260775671,
                  width: 61.55175533294688,
                },
                text: "천재들을 이끈",
              },
              {
                text: "오펜하이머 리더십",
                bbox: {
                  height: 430.86206359863326,
                  width: 61.55176311492922,
                  y: 2634.4138011726664,
                  x: 4160.8965327467495,
                },
              },
              {
                text: "무엇을 바라볼 것인기",
                bbox: {
                  x: 4144.493081125785,
                  y: 1623.601068806105,
                  height: 987.5719189453125,
                  width: 142.62327117919918,
                },
              },
              {
                bbox: {
                  width: 49.24139259338381,
                  y: 1403.3793123490375,
                  x: 4197.827579155032,
                  height: 184.65517456054675,
                },
                text: "박종규 지음",
              },
              {
                bbox: {
                  y: 935.586211927598,
                  height: 209.27585723876973,
                  width: 61.55173782348646,
                  x: 4197.827578902481,
                },
                text: "터닝페이지",
              },
              {
                bbox: {
                  width: 49.24139259338381,
                  y: 2979.103451503549,
                  height: 172.34482543945342,
                  x: 3853.137923777446,
                },
                text: "CULTURE",
              },
              {
                text: "우리 조직의 운영체제는 무엇인가",
                bbox: {
                  width: 61.55178451538113,
                  y: 1440.310356727586,
                  height: 713.9999999999999,
                  x: 3976.2413485061024,
                },
              },
              {
                bbox: {
                  width: 169.51674407958964,
                  height: 1051.666940917969,
                  y: 1105.0266701729463,
                  x: 3812.3110364145127,
                },
                text: "조직문화 통찰 김성제8",
              },
              {
                text: "GROWTH 그로스 경영자로 성장한다는 것",
                bbox: {
                  width: 98.4828863525393,
                  height: 1428.0000000000002,
                  y: 1785.0000178499995,
                  x: 3569.9999394095485,
                },
              },
              {
                bbox: {
                  x: 3606.931027024463,
                  y: 1317.2069024547532,
                  width: 61.55173782348646,
                  height: 209.27585723876973,
                },
                text: "조남성 지음",
              },
              {
                text: "왜 모두가 그 상사와 일하고 싶어하는가 • 홍석한 지음",
                bbox: {
                  height: 1862.273342285156,
                  x: 3318.302022972946,
                  y: 1229.4963648884034,
                  width: 155.8602987670899,
                },
              },
              {
                bbox: {
                  x: 3065.275833545649,
                  y: 2449.758621844312,
                  height: 726.3103491210932,
                  width: 160.03453857421877,
                },
                text: "| 리더의 오판",
              },
              {
                text: "A LEADER'S",
                bbox: {
                  width: 88.8738111877444,
                  y: 1870.6049929837898,
                  height: 530.8498608398437,
                  x: 3156.7605426003183,
                },
              },
              {
                text: "MISJUDGMENTS",
                bbox: {
                  y: 1868.584844791678,
                  width: 97.99661361694396,
                  x: 3071.9093367287314,
                  height: 545.967762451172,
                },
              },
              {
                bbox: {
                  y: 2481.8439161831566,
                  width: 179.29195678710923,
                  height: 685.6940661621094,
                  x: 2819.131681381466,
                },
                text: "애자일 컴퍼니",
              },
              {
                text: "PAM 변화에 민첩한 기업이 되는 5가지 원칙",
                bbox: {
                  x: 2890.9841885944093,
                  height: 829.433942871094,
                  y: 1450.4182931363575,
                  width: 108.2759820556637,
                },
              },
              {
                bbox: {
                  x: 2942.172406334809,
                  y: 1144.862076810128,
                  width: 61.55173976898158,
                  height: 196.96550811767543,
                },
                text: "정재상 지음",
              },
              {
                bbox: {
                  y: 1956.3917312182743,
                  height: 297.53865051269537,
                  width: 67.62246231079088,
                  x: 2737.8583041826832,
                },
                text: "아이디어는",
              },
              {
                bbox: {
                  y: 1904.2291048979398,
                  x: 2667.9079190455227,
                  width: 87.79715057373042,
                  height: 327.4426922607421,
                },
                text: "대회의실에서",
              },
              {
                bbox: {
                  x: 2622.1034410188254,
                  height: 160.0344763183596,
                  width: 49.24139259338381,
                  y: 2068.137936255044,
                },
                text: "죽는다",
              },
              {
                bbox: {
                  width: 207.62034942626946,
                  height: 1353.4162353515626,
                  x: 2331.1123548023197,
                  y: 1815.9881279412875,
                },
                text: "8 정부 조직에서의 코칭",
              },
              {
                bbox: {
                  height: 221.58620635986355,
                  width: 110.79311874389677,
                  x: 2708.275853805638,
                  y: 1501.8620704648959,
                },
                text: "물걸을 깨는",
              },
              {
                bbox: {
                  height: 209.27585723876973,
                  width: 61.55173782348615,
                  x: 2646.7241306266187,
                  y: 1514.1724188241499,
                },
                text: "다양성의 힘",
              },
              {
                bbox: {
                  height: 196.96550811767543,
                  x: 2659.0344751464463,
                  width: 61.55173782348646,
                  y: 1206.4138001273693,
                },
                text: "임병권 지음",
              },
              {
                text: "테오도라 1, 피츠시몬스 , 메리케이트 비한",
                bbox: {
                  width: 91.98960800170913,
                  x: 2519.1784111580628,
                  y: 1007.3519717102383,
                  height: 730.3260388183593,
                },
              },
              {
                text: "도허티",
                bbox: {
                  height: 135.41379364013662,
                  x: 2462.0689600044416,
                  y: 1501.8620709540696,
                  width: 61.551733932495296,
                },
              },
              {
                text: "•엘런 리 마이어스 지음",
                bbox: {
                  y: 1093.041959768949,
                  height: 423.2793933105468,
                  x: 2463.3583126819512,
                  width: 87.30830978393547,
                },
              },
              {
                bbox: {
                  x: 2396.31026620512,
                  width: 94.19572219848635,
                  height: 523.2448095703129,
                  y: 1105.166449818781,
                },
                text: "김진경• 박은희 이인화 육김",
              },
              {
                bbox: {
                  width: 151.47423339843755,
                  height: 919.5125683593752,
                  y: 1866.441710887915,
                  x: 2133.5722419314798,
                },
                text: "MBTI 유형별 리더십 & 팔로워십",
              },
              {
                text: "윤서영 지음",
                bbox: {
                  width: 59.294492111206026,
                  x: 2238.0456207048205,
                  height: 189.12891174316414,
                  y: 1376.2246179364527,
                },
              },
              {
                text: "S컨리이북스",
                bbox: {
                  width: 61.551741714477316,
                  x: 2265.103438872072,
                  y: 824.7931059324823,
                  height: 221.58620635986307,
                },
              },
              {
                text: "리더의 태도",
                bbox: {
                  width: 193.9498654174807,
                  x: 1932.2579312703642,
                  y: 1973.748868658391,
                  height: 669.4934106445315,
                },
              },
              {
                text: "-돕는 인간, 퍼실리테이터의 시대가 온다 김행숙 박승희 최은미 생선철 지음",
                bbox: {
                  height: 1806.9180468750003,
                  x: 1668.4156576955934,
                  width: 226.3141575622561,
                  y: 1325.738732357923,
                },
              },
              {
                bbox: {
                  height: 1122.8428198242186,
                  width: 227.57111114501947,
                  x: 1442.0332667627065,
                  y: 1777.8464136115701,
                },
                text: "착한 리더가 온다",
              },
              {
                text: "구성원과",
                bbox: {
                  width: 84.86068176269528,
                  y: 2642.08251354503,
                  x: 1335.1678762341537,
                  height: 194.84655212402336,
                },
              },
              {
                bbox: {
                  x: 1270.9344703192107,
                  height: 291.68277099609344,
                  y: 2642.017236116236,
                  width: 93.55865020751949,
                },
                text: "조직을 키우는",
              },
              {
                text: "이기적 리더 |",
                bbox: {
                  y: 1965.1711022266497,
                  x: 1290.3302410661734,
                  height: 661.9455725097657,
                  width: 175.6591484069822,
                },
              },
              {
                bbox: {
                  x: 1052.2322771905642,
                  width: 267.2852288818361,
                  y: 1737.0057822526835,
                  height: 1251.0033544921873,
                },
                text: "어서 와, 리더는 처음이지?",
              },
              {
                text: "김태윤 지음",
                bbox: {
                  y: 1428.0000010452973,
                  x: 1563.4137819080418,
                  width: 61.55174755096423,
                  height: 233.8965554809569,
                },
              },
              {
                bbox: {
                  y: 843.5613213134661,
                  height: 207.15688568115252,
                  x: 1579.35668171867,
                  width: 99.18911682128903,
                },
                text: "체인지업",
              },
              {
                bbox: {
                  y: 837.1034541788907,
                  height: 209.27585723876973,
                  width: 61.551737823486306,
                  x: 1415.689647714119,
                },
                text: "# 책세상",
              },
              {
                text: "• 김한솔 시금",
                bbox: {
                  y: 1501.8620848204957,
                  x: 1391.068954262713,
                  width: 36.93105514526381,
                  height: 258.5172225952152,
                },
              },
              {
                bbox: {
                  x: 1227.4687288145615,
                  height: 474.7972375488283,
                  y: 1239.6941885299989,
                  width: 100.5627923583984,
                },
                text: "리더가 된 사람들을 위한",
              },
              {
                text: "일과 사람 고민 이야기",
                bbox: {
                  width: 91.73110610961916,
                  x: 1170.0665472078786,
                  y: 1277.280729679746,
                  height: 436.5984741210937,
                },
              },
              {
                bbox: {
                  width: 61.55172225952147,
                  height: 184.65517456054675,
                  x: 1243.344827756015,
                  y: 1021.7586219244682,
                },
                text: "1",
              },
              {
                text: "책",
                bbox: {
                  x: 1255.6551727374806,
                  y: 861.7241413530973,
                  height: 73.86206359863264,
                  width: 61.55172225952147,
                },
              },
              {
                bbox: {
                  height: 1363.194140625,
                  x: 870.4119521132518,
                  y: 1497.0730377208226,
                  width: 181.4625384521484,
                },
                text: "부하직원이 말하지 않는 31가지 진실",
              },
              {
                bbox: {
                  width: 155.1486753845215,
                  x: 621.0736600910892,
                  height: 454.76425140380906,
                  y: 2295.663765799906,
                },
                text: "리더의 길",
              },
              {
                bbox: {
                  width: 87.31431747436525,
                  y: 1757.8169939862623,
                  height: 485.59016235351544,
                  x: 698.7652558714458,
                },
                text: "LEADER'S WAY | 신제구 지음",
              },
              {
                bbox: {
                  y: 2868.310360485493,
                  width: 49.241400375366226,
                  x: 406.24136795714446,
                  height: 295.44825439453103,
                },
                text: "까칠한 인재마저",
              },
              {
                bbox: {
                  x: 344.68964397700705,
                  y: 2868.310343690494,
                  height: 295.4482855224609,
                  width: 61.551745605468724,
                },
                text: "사로잡은 그들의",
              },
              {
                text: "지독한 솔직함",
                bbox: {
                  y: 2856.000011774744,
                  height: 320.0689526367187,
                  x: 283.1379173332705,
                  width: 73.8620947265625,
                },
              },
              {
                text: "실리콘밸리의 팀장들",
                bbox: {
                  width: 170.07769271850583,
                  y: 1682.753053333015,
                  height: 1028.9583068847658,
                  x: 336.655395922701,
                },
              },
            ],
          },
          osType: "ios",
        })
        break
      case "shelf-photo-analyze-id":
        cb(true, {
          id: "mock-analysis-id-1234567890",
        })
        break
      case "shelf-photo-finalize-id":
        cb(true, {
          id: "mock-finalize-id-1234567890",
        })
        break
      case "barcode-scan": {
        cb(true, {
          type: "ISBN",
          scanned: "9788936434595", // 채식주의자 ISBN
        })
        break
      }
      case "common/clipboard/copy":
        cb(true, {})
        break
      case "common/session/expire":
        cb(true, {
          status: "expired",
          message: "(mocking) 세션이 만료되었습니다.",
        })
        break
      case "common/session/extend":
        cb(true, {
          status: "extended",
          newExpiry: Date.now() + 10 * 60 * 1000, // 10분 연장
          message: "(mocking) 세션이 연장되었습니다.",
        })
        break
      case "common/log/dfinery":
        console.log(
          "[Dfinery Mock]",
          message.$param.eventName,
          message.$param.attrModel,
        )
        cb(true, { success: true })
        break
      case "common/app/version":
        cb(true, {
          version: "1.0.0 (mocking)",
          osType: "ios",
        })
        break
      default:
        console.log("Unknown path")
    }
  }
}

export const nativeBr =
  typeof window !== "undefined" && window.nativeBr
    ? window.nativeBr
    : new NativeBr(new NativeBrMocking())

if (typeof window !== "undefined") {
  window.nativeBr = nativeBr
}
